import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { HStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { LEProductAccessRowSelectionState } from './LEProductAccessRow';

const messages = defineMessages({
  entitiesSelectedCount: {
    id: 'Team.inviteTray.entityAccessFooter.entitiesSelectedCount',
    defaultMessage: '{selected} of {total} entities selected',
    description: 'Count of selected legal entities in the entity access footer',
  },
  selectAll: {
    id: 'Team.inviteTray.entityAccessFooter.selectAll',
    defaultMessage: 'Select all',
    description: 'Action to select all legal entities in the entity access footer',
  },
  deselectAll: {
    id: 'Team.inviteTray.entityAccessFooter.deselectAll',
    defaultMessage: 'Deselect all',
    description: 'Action to deselect all legal entities in the entity access footer',
  },
  toggleAllEntitiesAccessibilityLabel: {
    id: 'Team.inviteTray.entityAccessFooter.toggleAllEntitiesAccessibilityLabel',
    defaultMessage: 'Toggle all legal entities',
    description:
      'Accessibility label for selecting or deselecting all legal entities in the entity access footer',
  },
});

const ACTION_STYLE = { cursor: 'pointer' } as const;

type EntityRow = {
  selectionState: LEProductAccessRowSelectionState;
};

export type EntityAccessFooterParams = {
  entities: readonly { entityRow: EntityRow }[];
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

export function EntityAccessFooter({
  entities,
  onSelectAll,
  onDeselectAll,
}: EntityAccessFooterParams) {
  const { formatMessage } = useIntl();

  const selectedCount = useMemo(
    () => entities.filter((e) => e.entityRow.selectionState === 'indeterminate').length,
    [entities],
  );
  const totalCount = entities.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  return (
    <HStack alignItems="center" gap={1}>
      <Text as="span" font="label2" color="fgMuted">
        {formatMessage(messages.entitiesSelectedCount, {
          selected: selectedCount,
          total: totalCount,
        })}
      </Text>
      <Text as="span" font="label2" color="fgMuted">
        ·
      </Text>
      <Pressable
        onClick={allSelected ? onDeselectAll : onSelectAll}
        background="transparent"
        noScaleOnPress
        accessibilityLabel={formatMessage(messages.toggleAllEntitiesAccessibilityLabel)}
      >
        <Text
          as="span"
          font="label2"
          color="fgMuted"
          textDecoration="underline"
          style={ACTION_STYLE}
        >
          {formatMessage(allSelected ? messages.deselectAll : messages.selectAll)}
        </Text>
      </Pressable>
    </HStack>
  );
}
